import React, { useEffect, useMemo, useState, Fragment } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  Paper,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReportDataService from '../../../services/report.service';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatCurrencySync } from '../../../utils/currencyUtils';

const fmtHours = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return String(Math.round(n * 100) / 100);
};

const serviceDateKey = (r) => {
  const s = r?.service_date;
  if (!s) return '';
  return String(s).slice(0, 10);
};

const CleaningHoursWagesByCleanerReport = () => {
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [tab, setTab] = useState(0);
  const [wageBasis, setWageBasis] = useState('hourly');
  const [cleanerFilterId, setCleanerFilterId] = useState('');

  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const locale = language === 'es' ? 'es-ES' : 'en-US';

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (startDate) params.start_date = startDate.format('YYYY-MM-DD');
      if (endDate) params.end_date = endDate.format('YYYY-MM-DD');
      const res = await ReportDataService.getCleaningHoursWagesByCleaner(params);
      setData(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || t('reports.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cleanerFilterId === '') return;
    const exists = rows.some((r) => String(r.employee_id) === cleanerFilterId);
    if (!exists) setCleanerFilterId('');
  }, [rows, cleanerFilterId]);

  const cleanerOptions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (r.employee_id == null) return;
      const key = String(r.employee_id);
      if (!m.has(key)) m.set(key, r.employee_name || key);
    });
    return Array.from(m.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), locale));
  }, [rows, locale]);

  const filteredRows = useMemo(() => {
    if (cleanerFilterId === '') return rows;
    return rows.filter((r) => String(r.employee_id) === cleanerFilterId);
  }, [rows, cleanerFilterId]);

  const displaySummary = useMemo(() => {
    if (cleanerFilterId === '') {
      return {
        total_hours: Number(summary.total_hours) || 0,
        total_wages_by_hourly: Number(summary.total_wages_by_hourly) || 0,
        total_wages_by_job: Number(summary.total_wages_by_job) || 0,
      };
    }
    return filteredRows.reduce(
      (acc, r) => ({
        total_hours: acc.total_hours + (Number(r.total_hours) || 0),
        total_wages_by_hourly: acc.total_wages_by_hourly + (Number(r.wages_by_hourly) || 0),
        total_wages_by_job: acc.total_wages_by_job + (Number(r.wages_by_job) || 0),
      }),
      { total_hours: 0, total_wages_by_hourly: 0, total_wages_by_job: 0 }
    );
  }, [cleanerFilterId, filteredRows, summary]);

  const formattedRows = useMemo(() => {
    return filteredRows.map((r) => ({
      ...r,
      _dateLabel: r.service_date
        ? new Date(
            String(r.service_date).includes('T') ? r.service_date : `${String(r.service_date).slice(0, 10)}T12:00:00`
          ).toLocaleDateString(locale, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '',
    }));
  }, [filteredRows, locale]);

  const detailRowsByDay = useMemo(() => {
    if (!formattedRows.length) return [];
    const groups = [];
    let currentKey = null;
    let bucket = [];
    for (const r of formattedRows) {
      const k = serviceDateKey(r);
      if (k !== currentKey) {
        if (bucket.length) {
          groups.push({
            dateKey: currentKey,
            dateLabel: bucket[0]._dateLabel,
            rows: bucket,
          });
        }
        currentKey = k;
        bucket = [];
      }
      bucket.push(r);
    }
    if (bucket.length) {
      groups.push({
        dateKey: currentKey,
        dateLabel: bucket[0]._dateLabel,
        rows: bucket,
      });
    }
    return groups;
  }, [formattedRows]);

  const byCleanerAggregates = useMemo(() => {
    const m = new Map();
    filteredRows.forEach((r) => {
      const id = r.employee_id;
      if (id == null) return;
      const cur = m.get(id) || {
        employee_id: id,
        employee_name: r.employee_name || '',
        total_hours: 0,
        wages_by_hourly: 0,
        wages_by_job: 0,
      };
      cur.total_hours += Number(r.total_hours) || 0;
      cur.wages_by_hourly += Number(r.wages_by_hourly) || 0;
      cur.wages_by_job += Number(r.wages_by_job) || 0;
      m.set(id, cur);
    });
    return Array.from(m.values()).sort((a, b) => String(a.employee_name || '').localeCompare(String(b.employee_name || '')));
  }, [filteredRows]);

  const hasRows = formattedRows.length > 0;

  return (
    <Box>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
        {t('reports.cleaningHoursWagesByCleaner')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('reports.cleaningHoursWagesByCleanerDescription')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <DatePicker label={t('reports.startDate')} value={startDate} onChange={(v) => setStartDate(v)} />
              <DatePicker label={t('reports.endDate')} value={endDate} onChange={(v) => setEndDate(v)} />
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="cleaning-report-cleaner-filter">{t('reports.cleaningFilterByCleaner')}</InputLabel>
                <Select
                  labelId="cleaning-report-cleaner-filter"
                  label={t('reports.cleaningFilterByCleaner')}
                  value={cleanerFilterId}
                  onChange={(e) => setCleanerFilterId(e.target.value)}
                  disabled={loading || !rows.length}
                >
                  <MenuItem value="">{t('reports.cleaningFilterAllCleaners')}</MenuItem>
                  {cleanerOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={load} disabled={loading}>
                {t('reports.filter')}
              </Button>
            </Box>
          </LocalizationProvider>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {hasRows && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('reports.cleaningWageBasisLabel')}
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    value={wageBasis}
                    exclusive
                    onChange={(_, v) => v && setWageBasis(v)}
                  >
                    <ToggleButton value="hourly">{t('reports.cleaningWageBasisHourly')}</ToggleButton>
                    <ToggleButton value="job">{t('reports.cleaningWageBasisJob')}</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab label={t('reports.cleaningTabDetail')} />
                <Tab label={t('reports.cleaningTabByCleaner')} />
              </Tabs>

              {tab === 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('reports.date')}</TableCell>
                        <TableCell>{t('reports.cleaningCleaner')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningHours')}</TableCell>
                        <TableCell>{t('reports.cleaningJobName')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningWagesByHourly')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningWagesByJob')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!hasRows ? (
                        <TableRow>
                          <TableCell colSpan={6}>{t('reports.noData')}</TableCell>
                        </TableRow>
                      ) : (
                        detailRowsByDay.map((g, gi) => {
                          const subH = g.rows.reduce((a, r) => a + (Number(r.total_hours) || 0), 0);
                          const subWh = g.rows.reduce((a, r) => a + (Number(r.wages_by_hourly) || 0), 0);
                          const subWj = g.rows.reduce((a, r) => a + (Number(r.wages_by_job) || 0), 0);
                          return (
                            <Fragment key={`day-${g.dateKey}-${gi}`}>
                              {g.rows.map((r) => (
                                <TableRow key={`${r.employee_id}-${r.service_date}-${r.cleaning_detail_id ?? ''}`}>
                                  <TableCell>{r._dateLabel}</TableCell>
                                  <TableCell>{r.employee_name}</TableCell>
                                  <TableCell align="right">{fmtHours(r.total_hours)}</TableCell>
                                  <TableCell>{r.job_name || '—'}</TableCell>
                                  <TableCell align="right">{formatCurrencySync(r.wages_by_hourly || 0)}</TableCell>
                                  <TableCell align="right">{formatCurrencySync(r.wages_by_job || 0)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow
                                sx={{
                                  bgcolor: 'action.hover',
                                  '& td': { py: 0.75, borderTop: '1px solid', borderColor: 'divider' },
                                }}
                              >
                                <TableCell colSpan={2}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    {t('reports.cleaningDaySubtotal')}
                                    {g.dateLabel ? ` · ${g.dateLabel}` : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="caption" fontWeight={600}>
                                    {fmtHours(subH)}
                                  </Typography>
                                </TableCell>
                                <TableCell />
                                <TableCell align="right">
                                  <Typography variant="caption" fontWeight={600}>
                                    {formatCurrencySync(subWh)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="caption" fontWeight={600}>
                                    {formatCurrencySync(subWj)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </Fragment>
                          );
                        })
                      )}
                    </TableBody>
                    {hasRows && (
                      <TableFooter>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>
                            {t('common.total')}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {fmtHours(displaySummary.total_hours)}
                          </TableCell>
                          <TableCell sx={{ borderTop: '2px solid', borderColor: 'divider' }} />
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {formatCurrencySync(displaySummary.total_wages_by_hourly || 0)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {formatCurrencySync(displaySummary.total_wages_by_job || 0)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              )}

              {tab === 1 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('reports.cleaningCleaner')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningHours')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningWagesByHourly')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningWagesByJob')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningAmountToReceive')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!hasRows ? (
                        <TableRow>
                          <TableCell colSpan={5}>{t('reports.noData')}</TableCell>
                        </TableRow>
                      ) : (
                        byCleanerAggregates.map((r) => (
                          <TableRow key={r.employee_id}>
                            <TableCell>{r.employee_name}</TableCell>
                            <TableCell align="right">{fmtHours(r.total_hours)}</TableCell>
                            <TableCell align="right">{formatCurrencySync(r.wages_by_hourly || 0)}</TableCell>
                            <TableCell align="right">{formatCurrencySync(r.wages_by_job || 0)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatCurrencySync(
                                wageBasis === 'hourly' ? r.wages_by_hourly || 0 : r.wages_by_job || 0
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                    {hasRows && (
                      <TableFooter>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>
                            {t('common.total')}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {fmtHours(displaySummary.total_hours)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {formatCurrencySync(displaySummary.total_wages_by_hourly || 0)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {formatCurrencySync(displaySummary.total_wages_by_job || 0)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {formatCurrencySync(
                              wageBasis === 'hourly'
                                ? displaySummary.total_wages_by_hourly || 0
                                : displaySummary.total_wages_by_job || 0
                            )}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CleaningHoursWagesByCleanerReport;
